import { useState } from 'react';
import { IconUpload, IconPalette } from '@/components/ui/Icon';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { dialog } from '@/shared/store/dialogStore';
import { registerWidget, type WidgetRenderProps, type WidgetConfigProps } from '../registry';

// Personal image tile (e.g. a family photo). The picked file is downscaled in
// the browser and stored as a data URL inside the widget config — no upload
// endpoint needed, and the app CSP already allows img-src data:. Kept small so
// the saved layout JSON stays lean.
function downscaleToDataUrl(file: File, maxDim = 800, quality = 0.82): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const longest = Math.max(width, height);
      if (longest > maxDim) {
        const s = maxDim / longest;
        width = Math.round(width * s);
        height = Math.round(height * s);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('canvas unavailable')); return; }
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('could not load image')); };
    img.src = url;
  });
}

function ImageRender({ config, widgetId }: WidgetRenderProps) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const src = typeof config.src === 'string' ? config.src : '';
  // Direct set: pick a file straight on the tile — downscale, merge into the
  // saved layout by widget id, persist. No Customize round-trip needed.
  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !widgetId) return;
    setBusy(true);
    try {
      const url = await downscaleToDataUrl(file);
      const resp = await api.get<{ layout: { version: number; widgets: Array<{ id: string; config?: Record<string, unknown> }> } }>('/home/layout');
      const layout = resp.layout;
      layout.widgets = layout.widgets.map((w) => w.id === widgetId ? { ...w, config: { ...(w.config || {}), src: url } } : w);
      await api.put('/home/layout', layout);
      await qc.invalidateQueries({ queryKey: ['home-layout'] });
    } catch {
      dialog.error('Could not save that picture.');
    } finally { setBusy(false); }
  };
  if (!src) {
    return (
      <label className="flex flex-col items-center justify-center h-full text-muted gap-1.5 text-[0.75rem] text-center cursor-pointer hover:text-secondary transition-colors">
        <IconPalette size={22} />
        <span>{busy ? 'Saving…' : widgetId ? 'Click to pick a picture' : 'Pick a picture via Configure'}</span>
        {widgetId && <input type="file" accept="image/*" className="hidden" onChange={onPick} />}
      </label>
    );
  }
  const fit = config.fit === 'contain' ? 'object-contain' : 'object-cover';
  return <img src={src} alt="" draggable={false} className={`w-full h-full ${fit} rounded-md select-none`} />;
}

function ImageConfig({ config, onChange }: WidgetConfigProps) {
  const src = typeof config.src === 'string' ? config.src : '';
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setErr(''); setBusy(true);
    try {
      const url = await downscaleToDataUrl(file);
      onChange({ ...config, src: url });
    } catch {
      setErr('Could not read that picture.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-2 text-[0.8125rem]">
      {src && <img src={src} alt="" className="w-full h-24 object-cover rounded-md border border-border-light" />}
      <label className="inline-flex items-center gap-2 h-8 px-3 rounded-md border border-border bg-surface hover:bg-row-highlight cursor-pointer w-fit">
        <IconUpload size={14} /> {src ? 'Replace picture' : 'Choose picture…'}
        <input type="file" accept="image/*" className="hidden" onChange={onFile} />
      </label>
      {busy && <span className="text-[0.75rem] text-muted">Processing…</span>}
      {err && <span className="text-[0.75rem] text-danger">{err}</span>}
      {src && (
        <div className="grid gap-1">
          <label className="text-[0.75rem] text-muted">Display</label>
          <select className="h-[30px] px-2 rounded-md border border-border bg-surface text-[0.8125rem] w-fit"
            value={config.fit === 'contain' ? 'contain' : 'cover'}
            onChange={(e) => onChange({ ...config, fit: e.target.value })}>
            <option value="cover">Fill the tile (crop to fit)</option>
            <option value="contain">Show the whole picture</option>
          </select>
        </div>
      )}
      {src && (
        <button type="button" onClick={() => onChange({ ...config, src: '' })}
          className="text-[0.75rem] text-danger hover:underline w-fit">Remove picture</button>
      )}
      <span className="text-meta text-muted">Any JPG/PNG. Large photos are downscaled and saved with your layout.</span>
    </div>
  );
}

registerWidget({
  type: 'image',
  label: 'Picture',
  defaultSize: 'S',
  compact: true,
  defaultWH: { w: 3, h: 3 },
  icon: <IconPalette size={13} />,
  accent: '#1f8ba3',
  ConfigForm: ImageConfig,
  Render: ImageRender,
});
